/**
 *
 * Module:        Component
 * Name:          Guru Resizable Sidebar
 * Description:   Provides a shared accessible width splitter for Guru sidebars
 * Developers:    Adrian Engelmann, Kiran Jayaramu, Nicu Bratescu, Daniela Popescu
 * Copyright:     Copyright 2026 by Qalgo GmbH (www.qalgo.de). All rights reserved.
 *
 */

import React, {useEffect, useRef} from 'react';
import Box from '@mui/material/Box';

const clamp = (value, minimum, maximum) => Math.min(maximum, Math.max(minimum, value));

export default function GuruResizableSidebar({side, width, minWidth, maxWidth, label, onResize, component='aside', showBorder=true, children}) {
	const drag = useRef(null);

	useEffect(() => {
		const handlePointerMove = event => {
			if (!drag.current) return;
			const movement = side === 'left'
				? event.clientX - drag.current.startX
				: drag.current.startX - event.clientX;
			onResize(clamp(drag.current.startWidth + movement, minWidth, maxWidth));
		};
		const handlePointerUp = () => { drag.current = null; };
		window.addEventListener('pointermove', handlePointerMove);
		window.addEventListener('pointerup', handlePointerUp);
		return () => {
			window.removeEventListener('pointermove', handlePointerMove);
			window.removeEventListener('pointerup', handlePointerUp);
		};
	}, [maxWidth, minWidth, onResize, side]);

	const resizeWithKeyboard = event => {
		const direction = side === 'left' ? 1 : -1;
		if (event.key === 'ArrowLeft') {
			event.preventDefault();
			onResize(clamp(width - (12 * direction), minWidth, maxWidth));
		}
		if (event.key === 'ArrowRight') {
			event.preventDefault();
			onResize(clamp(width + (12 * direction), minWidth, maxWidth));
		}
	};

	return (
		<Box
			component={component}
			sx={{
				position: 'relative',
				flex: `0 0 ${width}px`,
				width,
				minWidth: width,
				maxWidth: width,
				minHeight: 0,
				overflow: 'hidden',
				boxSizing: 'border-box',
				borderRight: showBorder && side === 'left' ? '1px solid' : 0,
				borderLeft: showBorder && side === 'right' ? '1px solid' : 0,
				borderColor: 'divider'
			}}
		>
			{children}
			<Box
				role="separator"
				tabIndex={0}
				aria-label={label}
				aria-orientation="vertical"
				aria-valuemin={minWidth}
				aria-valuemax={maxWidth}
				aria-valuenow={width}
				onPointerDown={event => {
					event.preventDefault();
					drag.current = {startX: event.clientX, startWidth: width};
				}}
				onKeyDown={resizeWithKeyboard}
				sx={{
					position: 'absolute',
					top: 0,
					bottom: 0,
					[side === 'left' ? 'right' : 'left']: 0,
					width: 8,
					zIndex: 30,
					cursor: 'col-resize',
					touchAction: 'none',
					outline: 'none',
					'&::after': {
						content: '""',
						position: 'absolute',
						top: 0,
						bottom: 0,
						left: '50%',
						width: 2,
						transform: 'translateX(-50%)',
						backgroundColor: 'transparent',
						transition: 'background-color 120ms'
					},
					'&:hover::after, &:focus-visible::after': {
						backgroundColor: 'var(--primary-color, #004c3f)'
					}
				}}
			/>
		</Box>
	);
}
